/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {HVSRecord} from '@ion/core/src/types/entities';
import {timeDiff} from '@ion/core/src/utils/DateUtil';

// Check if the event is within the last 180 days
export const THRESHOLD_DAYS = 180;

/**
 * Checks if the history for a given asset record is available.
 *
 * History is considered available if the record's `eventTime` exists and
 * is within a defined threshold (e.g., 180 days) from the current date.
 *
 * ```typescript
 * 
 * const record = { eventTime: "2023-09-01T12:00:00Z" };
 * const available = isHistoryAvailable(record);
 * // available === true (if within 180 days)
 * 
 * ```
 * @param record - The asset record containing an `eventTime` field.
 * @returns `true` if history is available (event time is within the threshold), otherwise `false`.
 *
 */
export const isHistoryAvailable = (record: HVSRecord) => {
  const {eventTime} = record || {};
  if (!eventTime) {
    return false;
  }
  return timeDiff(new Date(), new Date(eventTime)) <= THRESHOLD_DAYS;
};
